import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// @mui
import {
  Box,
  Button,
  Card,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Tabs,
  Tooltip,
} from '@mui/material';
// hooks
import useTabs from '@/hooks/useTabs';
import useSettings from '@/hooks/useSettings';
import useToggle from '@/hooks/useToggle';
import useTable from '@/hooks/useTable'; // We'll rely on server-side pagination, so we won't use getComparator, emptyRows anymore
import { useGetAccess } from '@/hooks/useAccess';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
// components
import {
  HeaderBreadcrumbs,
  Iconify,
  Label,
  Page,
  Scrollbar,
} from '@/components';
import Dialog from '@/components/dialog/Dialog';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TableSelectedActions,
} from '@/components/table';
// custom components
import {
  BookingsTableRow,
  BookingsTableToolbar,
  BookingsListSkeletons,
} from '../components';
// API
import { deleteBooking, getBookings } from '../api';
import { bookingsManagementPath } from '../routes/paths';
import { getSettingsData } from '@/features/app/api';
// utils
import {
  BOOKINGS_TABLE_HEAD,
  calculateBookingStatusFiguresNew,
} from '../utils'; // Remove applySortFilterBookings since filtering is handled server-side
// redux
import { dispatch } from '@/stores';
import { resetReduxValues } from '@/features/bookings-management/slices';
// types
import { Booking } from '../types';
import { LabelColors } from '@/types';

export default function BookingsList() {
  const { themeStretch } = useSettings();
  const navigate = useNavigate();
  const { errorMsg, successMsg } = useSnackbarMsg();
  const allow = useGetAccess('bookings');
  const queryClient = useQueryClient();

  const [searchFieldValue, setSearchFieldValue] = useState('');
  const [filterServiceId, setFilterServiceId] = useState<number>(0);
  const [filterServiceTypeId, setFilterServiceTypeId] = useState<number>(0);
  const [filterStartDate, setFilterStartDate] = useState<string | null>(null);
  const [filterEndDate, setFilterEndDate] = useState<string | null>(null);

  const {
    dense,
    page,
    order, // not used if sorting is done server-side (optional)
    orderBy, // not used if sorting is done server-side (optional)
    rowsPerPage,
    setPage,
    selected,
    setSelected,
    onSelectRow,
    onSelectAllRows,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable({ defaultOrder: 'desc', defaultOrderBy: 'id' });

  const resetFilters = () => {
    setSearchFieldValue('');
    setFilterServiceId(0);
    setFilterServiceTypeId(0);
    setFilterStartDate(null);
    setFilterEndDate(null);
  };

  // SETTINGS
  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: () => getSettingsData(),
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching settings data `);
    },
    staleTime: 24 * 60 * 60 * 1000,
    cacheTime: 24 * 60 * 60 * 1000,
  });

  // BOOKING STATUS OPTIONS
  const bookingStatusOptions = (settingsData?.bookingStatuses || []).map(
    (status) => status.display ?? ''
  );
  bookingStatusOptions.push('All');

  const bookingStatusColors = (settingsData?.bookingStatuses || []).reduce<
    Record<string, string>
  >((acc, status) => {
    if (status.display) {
      acc[status.display.toLowerCase()] = status.style_class;
    }
    return acc;
  }, {});

  const toLabelColor = (value?: string): LabelColors => {
    const labelColors: LabelColors[] = [
      'default',
      'error',
      'info',
      'primary',
      'secondary',
      'success',
      'warning',
    ];
    if (value && labelColors.includes(value as LabelColors)) {
      return value as LabelColors;
    }
    return 'default';
  };

  const getStatusVariant = (status: string) =>
    status.toLowerCase() === 'new' ? 'filled' : 'ghost';

  // SERVICES MAPPING
  const servicesWithTheirVariants =
    settingsData?.services?.map((service) => ({
      id: service.id,
      name: service.name,
      variants: settingsData.serviceVariants.reduce<number[]>(
        (variantIDs, variant) => {
          if (variant.service_id === service.id) {
            variantIDs.push(variant.id);
          }
          return variantIDs;
        },
        []
      ),
    })) ?? [];

  const { currentTab: filterStatus, onChangeTab: onChangeFilterStatus } =
    useTabs('All');

  // QUERIES: Now we fetch bookings with pagination and filters
  const { data: bookingsData, isLoading: isBookingsDataLoading } = useQuery({
    queryKey: [
      'bookings',
      {
        page,
        rowsPerPage,
        searchFieldValue,
        filterServiceId,
        filterServiceTypeId,
        filterStartDate,
        filterEndDate,
        filterStatus,
      },
    ],
    queryFn: () =>
      getBookings({
        page: page + 1, // If your API is 1-indexed, adjust accordingly
        limit: rowsPerPage,
        search: searchFieldValue,
        serviceId: filterServiceId,
        serviceTypeId: filterServiceTypeId,
        startDate: filterStartDate ?? undefined,
        endDate: filterEndDate ?? undefined,
        status: filterStatus === 'All' ? undefined : filterStatus.toUpperCase(),
      }),
    keepPreviousData: true,
    enabled: !!settingsData,
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching bookings data `);
    },
  });

  const bookings = bookingsData?.data ?? [];
  const totalBookings = bookingsData?.total ?? 0;
  const statusCounts = bookingsData?.statusCounts ?? {};

  // DELETE MUTATION
  const deleteBookingMutation = useMutation({
    mutationFn: (bookingId: number) => deleteBooking(bookingId),
    onSuccess: (_apiResponse, variables) => {
      queryClient.invalidateQueries(['bookings']);
      successMsg(`Booking succesfully deleted`);
    },
    onError: (error) => {
      errorMsg(error, `There was an error while deleting booking`);
    },
  });

  const handleDeleteRows = async (selected: string[]) => {
    // Implement bulk delete if your API supports it
  };

  const handleEditRow = (bookingId: number) => {
    navigate(
      bookingsManagementPath('edit', {
        bookingId: String(bookingId),
      })
    );
  };

  const handleSearchField = (filterName: string) => {
    setSearchFieldValue(filterName);
    setPage(0);
  };

  const handleFilterDropdown = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    switch (name) {
      case 'service':
        setFilterServiceId(Number(value));
        setFilterServiceTypeId(0);
        break;
      case 'service_type':
        setFilterServiceTypeId(Number(value));
        break;
      case 'start_date':
        setFilterStartDate(value);
        break;
      case 'end_date':
        setFilterEndDate(value);
        break;
      default:
      // handle error
    }
    setSelected([]);
    setPage(0);
  };

  const handleDatepickerChange = (date: string | null, date_type: string) => {
    switch (date_type) {
      case 'start':
        setFilterStartDate(date);
        break;
      case 'end':
        setFilterEndDate(date);
        break;
      default:
      // handle error
    }
    setSelected([]);
    setPage(0);
  };

  const isNotFound =
    !isBookingsDataLoading && bookings.length === 0 && !!searchFieldValue;

  const {
    toggle: isDeleteDialogOpen,
    setToggle: setIsDeleteDialogOpen,
    onClose: closeDeleteDialog,
  } = useToggle();

  return (
    <Page title="Bookings Management">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Bookings Management"
          links={[
            { name: 'Dashboard', href: '' },
            { name: 'Bookings', href: '' },
          ]}
          action={
            <Button
              variant="contained"
              component={RouterLink}
              to={bookingsManagementPath('new')}
              startIcon={<Iconify icon={'eva:plus-fill'} />}
              onClick={() => dispatch(resetReduxValues())}
              disabled={isBookingsDataLoading}
            >
              New Booking
            </Button>
          }
        />

        <Card>
          {isBookingsDataLoading ? (
            <BookingsListSkeletons />
          ) : (
            <>
              <Tabs
                allowScrollButtonsMobile
                variant="scrollable"
                scrollButtons="auto"
                value={filterStatus}
                onChange={onChangeFilterStatus}
                sx={{ px: 2, bgcolor: 'background.neutral' }}
              >
                {bookingStatusOptions.map((tab, index) => (
                  <Tab
                    disableRipple
                    key={index}
                    label={tab}
                    value={tab}
                    icon={
                      <Label
                        color={toLabelColor(
                          bookingStatusColors[tab.toLowerCase()]
                        )}
                        variant={getStatusVariant(tab)}
                      >
                        {calculateBookingStatusFiguresNew(
                          tab,
                          statusCounts ?? []
                        )}
                      </Label>
                    }
                  />
                ))}
              </Tabs>

              <Divider />

              <BookingsTableToolbar
                filterService={filterServiceId}
                filterServiceType={filterServiceTypeId}
                filterSearchBar={searchFieldValue}
                filterStartDate={filterStartDate}
                filterEndDate={filterEndDate}
                handleFilterDropdown={handleFilterDropdown}
                handleSearchField={handleSearchField}
                handleDatepickerChange={handleDatepickerChange}
                resetFilters={resetFilters}
                settingsData={settingsData}
              />

              <Scrollbar>
                <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
                  {selected.length > 0 && (
                    <TableSelectedActions
                      sx={{ height: 60 }}
                      dense={dense}
                      numSelected={selected.length}
                      rowCount={bookings.length}
                      onSelectAllRows={(checked) =>
                        onSelectAllRows(
                          checked,
                          bookings.map((row) => row.id.toString())
                        )
                      }
                      actions={
                        <Stack direction="row" gap={1}>
                          {allow.canUpdate && (
                            <Tooltip title="Assign Cleaner">
                              <IconButton
                                color="info"
                                onClick={() =>
                                  alert('Need API to batch assign cleaners')
                                }
                                disabled={!allow.canDelete}
                              >
                                <Iconify icon={'eva:person-add-outline'} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {filterStatus !== 'removed' && allow.canDelete && (
                            <Tooltip title="Delete">
                              <IconButton
                                color="error"
                                onClick={() => setIsDeleteDialogOpen(true)}
                              >
                                <Iconify icon={'eva:trash-2-outline'} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      }
                    />
                  )}

                  <Table size={dense ? 'small' : 'medium'}>
                    <TableHeadCustom
                      sx={{ pt: 1 }}
                      order={order}
                      orderBy={orderBy}
                      headLabel={BOOKINGS_TABLE_HEAD}
                      rowCount={totalBookings}
                      numSelected={selected.length}
                      onSelectAllRows={(checked) =>
                        onSelectAllRows(
                          checked,
                          bookings.map((row) => row.id.toString())
                        )
                      }
                    />
                    <TableBody>
                      {bookings.map((booking) => (
                        <BookingsTableRow
                          permissions={allow}
                          key={booking.id}
                          row={booking}
                          selected={selected.includes(booking.id.toString())}
                          onSelectRow={() => onSelectRow(booking.id.toString())}
                          onDeleteRow={() =>
                            deleteBookingMutation.mutate(booking.id)
                          }
                          onEditRow={() => handleEditRow(booking.id)}
                        />
                      ))}

                      {/* No need to show TableEmptyRows if all rows are fetched page-wise */}
                      <TableNoData
                        isNotFound={isNotFound}
                        title="No bookings found"
                      />
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              <Box sx={{ position: 'relative' }}>
                <TablePagination
                  rowsPerPageOptions={[25, 50, 100, 150]}
                  component="div"
                  count={totalBookings}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={onChangePage}
                  onRowsPerPageChange={onChangeRowsPerPage}
                />

                <FormControlLabel
                  control={<Switch checked={dense} onChange={onChangeDense} />}
                  label="Dense"
                  sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
                />
              </Box>

              {/* DELETE BOOKINGS DIALOG */}
              <Dialog
                dialogTitle="Delete bookings"
                dialogText={`Are you sure you want to delete 
                  ${selected.length} ${
                    selected.length === 1 ? 'booking' : 'bookings'
                  }?`}
                dialogAgree={() => handleDeleteRows(selected)}
                dialogDisagree={closeDeleteDialog}
                isDialogOpen={isDeleteDialogOpen}
                setIsDialogOpen={setIsDeleteDialogOpen}
                handleCloseDialog={closeDeleteDialog}
                agreeButtonText="Delete"
                disagreeButtonText="Cancel"
                agreeButtonColor="error"
                disagreeButtonColor="inherit"
              />
            </>
          )}
        </Card>
      </Container>
    </Page>
  );
}
